import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, IconButton, Tab, Tabs, Typography } from '@mui/material';
import { RestartAlt as ResetIcon } from '@mui/icons-material';

import InvoicePreview from '../../components/layout/billings/invoices/InvoicePreview';
import InvoicePayments from '../../components/layout/billings/invoices/InvoicePayments';
import PageContainer from '../../components/layout/PageContainer';
import { FORTA_BLUE } from '../../utils/colors';
import { TabPanel, a11yProps } from './common';
import { claimsInvoiceActions } from '../../store';
import { FHConfirmDialog, FProgressBar } from '../../components/base';
import { InvoiceStatus, Roles } from '../../constants';
import { areUserRolesIn } from '../../helpers';

import type { RootState } from '../../store';
import type { SyntheticEvent } from 'react';

function InvoiceDetails() {
  const { invoiceId } = useParams();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const { current: invoiceData, loading: invoiceLoading } = useSelector(
    (state: RootState) => state.claimsInvoice,
  );
  const { user } = useSelector((state: RootState) => state.auth);
  const { pdfDownloading } = useSelector((state: RootState) => state.claimsInvoice);

  const [tab, setTab] = useState<number>(0);
  const [showResetDialog, setShowResetDialog] = useState(false);

  const isEditable =
    invoiceData?.status === InvoiceStatus.IN_PROGRESS &&
    areUserRolesIn(user?.roles, [Roles.InvoiceCoordinator]);

  useEffect(() => {
    if (!invoiceId) return;
    dispatch(
      claimsInvoiceActions.getInvoiceById({
        getAccessTokenSilently,
        invoiceId,
        expand: ['payments'],
      }),
    );
  }, [invoiceId]);

  const handleResetInvoiceStatus = () => {
    setShowResetDialog(false);
    dispatch(
      claimsInvoiceActions.resetInvoiceStatus({
        getAccessTokenSilently,
        invoiceId,
      }),
    );
  };

  const handlePDFDownload = async () => {
    const response = await dispatch(
      claimsInvoiceActions.downloadInvoicePDF({
        invoiceId: invoiceData?.id,
        getAccessTokenSilently,
      }),
    );

    if (response.payload?.document_url) {
      window.location.href = response.payload?.document_url;
    }
  };

  return (
    <PageContainer sx={{ py: 3 }}>
      {(invoiceLoading || pdfDownloading) && <FProgressBar />}
      {invoiceData ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Tabs
              value={tab}
              onChange={(_: SyntheticEvent, newValue: number) => {
                setTab(newValue);
              }}
              aria-label="Invoice tabs"
              sx={{
                minHeight: 32,
                '& .MuiTab-root': {
                  padding: '0 0.5rem',
                  textTransform: 'none',
                  fontWeight: 400,
                  minHeight: 32,
                  color: FORTA_BLUE.primary,
                  marginRight: '1.5rem',
                },
              }}
            >
              <Tab label="Invoice details" {...a11yProps(0)} data-testid="buildInvoiceTab" />
              <Tab label="Payments breakup" {...a11yProps(1)} data-testid="invoicePreviewTab" />
            </Tabs>

            {areUserRolesIn(user?.roles, [Roles.Admin]) &&
              invoiceData?.status !== InvoiceStatus.IN_PROGRESS && (
                <IconButton
                  sx={{
                    width: 190,
                    height: 36,
                    border: `2px solid ${FORTA_BLUE.primary}`,
                    borderRadius: '20px',
                    backgroundColor: 'transparent',
                    gap: 1,
                  }}
                  disabled={invoiceLoading}
                  onClick={() => setShowResetDialog(true)}
                >
                  <ResetIcon width={16} sx={{ color: FORTA_BLUE.primary }} />
                  <Typography
                    sx={{
                      color: FORTA_BLUE.primary,
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    Reset invoice status
                  </Typography>
                </IconButton>
              )}
          </Box>
          <TabPanel value={tab} index={0}>
            <InvoicePreview invoiceData={invoiceData} handlePDFDownload={handlePDFDownload} />
          </TabPanel>
          <TabPanel value={tab} index={1}>
            <InvoicePayments
              invoiceLoading={invoiceLoading}
              isSentToGuardians={Boolean(invoiceData.date_sent)}
              amountDue={invoiceData?.amount_due ?? 0}
              invoiceId={invoiceId}
              isEditable={isEditable}
              handlePDFDownload={handlePDFDownload}
              invoicePayments={invoiceData?.payments || []}
            />
          </TabPanel>
          <FHConfirmDialog
            open={showResetDialog}
            title={'Reset Invoice Status'}
            content={
              'Are you sure you want to reset the invoice status? This will change the status of this invoice to In-progress'
            }
            submittext={'Yes, Reset'}
            canceltext={'Cancel'}
            disableSubmitButton={invoiceLoading}
            onClose={() => setShowResetDialog(false)}
            onSubmit={handleResetInvoiceStatus}
          />
        </>
      ) : null}
    </PageContainer>
  );
}

export default InvoiceDetails;
